<template>
  <div class="w-full relative">

    <div v-if="loading" class="flex flex-col gap-10">
      <div
        v-for="block in 2"
        :key="`block-${block}`"
        class="flex flex-col gap-4"
      >
        <div class="flex items-center gap-3">
          <div class="animate-pulse bg-white/20 rounded-full h-6 w-20"></div>
          <div class="animate-pulse bg-white/20 rounded h-6 w-64"></div>
          <div class="animate-pulse bg-white/20 rounded h-4 w-40"></div>
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          <div
            v-for="i in 5"
            :key="`kpi-${block}-${i}`"
            class="bg-white p-4 rounded-xl shadow-md flex flex-col items-center justify-center"
          >
            <div
              class="animate-pulse bg-gray-200 rounded-full w-24 h-24 mb-3"
            ></div>
            <div class="animate-pulse bg-gray-200 rounded h-3 w-20"></div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="i in 2"
            :key="`chart-${block}-${i}`"
            class="bg-white p-6 rounded-xl shadow-md flex flex-col items-center"
          >
            <div class="animate-pulse bg-gray-200 rounded h-4 w-40 mb-4"></div>
            <div
              class="animate-pulse bg-gray-200 rounded-full w-40 h-40 mb-4"
            ></div>
            <div class="flex gap-3 w-full justify-center">
              <div class="animate-pulse bg-gray-200 rounded h-3 w-16"></div>
              <div class="animate-pulse bg-gray-200 rounded h-3 w-16"></div>
              <div class="animate-pulse bg-gray-200 rounded h-3 w-16"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="error" class="text-center text-gray-400 py-10 text-sm">
      {{ error }}
    </div>

    <template v-else>
      <div
        ref="scrollContainer"
        class="overflow-y-auto pr-2"
        style="max-height: calc(100vh - 280px)"
        @scroll="onScroll"
      >
        <div v-if="groups.length > 0" class="mb-8">
          <h2 class="text-lg font-semibold text-white mb-4">
            {{ $t("user_progression.stats.group") }}s
          </h2>
          <StatsBlock
            v-for="entity in groups"
            :key="entity.id"
            :entity="entity"
          />
        </div>

        <div v-if="services.length > 0">
          <h2 class="text-lg font-semibold text-white mb-4">
            {{ $t("user_progression.stats.service") }}s
          </h2>
          <StatsBlock
            v-for="entity in services"
            :key="entity.id"
            :entity="entity"
          />
        </div>

        <div
          v-if="groups.length === 0 && services.length === 0"
          class="text-center text-gray-400 py-10 text-sm"
        >
          {{ $t("user_progression.stats.group") }} —
          {{ $t("user_progression.stats.service") }}
        </div>
      </div>

      <transition name="fade">
        <div
          v-if="showScrollIndicator"
          class="flex justify-center cursor-pointer"
          @click="scrollDown"
        >
          <div class="flex flex-col items-center animate-bounce">
            <ChevronDown :size="24" />
            <span class="text-xs text-gray-400 mb-1">Scroll</span>
          </div>
        </div>
      </transition>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from "vue";
import StatsBlock from "./StatsBlock.vue";
import { fetchStatsByEntity } from "@/services/userFollowService";
import { ChevronDown } from "lucide-vue-next";

const groups = ref([]);
const services = ref([]);
const loading = ref(false);
const error = ref(null);

const scrollContainer = ref(null);
const showScrollIndicator = ref(false);

const checkScrollable = () => {
  const el = scrollContainer.value;
  if (!el) return;
  const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
  const hasOverflow = el.scrollHeight > el.clientHeight;
  showScrollIndicator.value = hasOverflow && !isAtBottom;
};

const onScroll = () => {
  checkScrollable();
};

const scrollDown = () => {
  const el = scrollContainer.value;
  if (!el) return;
  el.scrollBy({ top: 300, behavior: "smooth" });
};

const mapEntity = (item, type) => ({
  id: item.id,
  name: item.name,
  type,
  responsible: item.responsible_name,
  total_users: item.total_users,
  active_users: item.total_active_users,
  cyberscore: item.cyberscore,
  progression: item.progression,
  progression_pluzz: item.progression_pluzz,
  users_started: item.users_started_modules,
  users_over_50: item.users_above_50_percent,
  users_completed: item.users_completed_all_modules,
  certified_users: item.certified_users_count,
});

const loadData = async () => {
  loading.value = true;
  error.value = null;
  try {
    const response = await fetchStatsByEntity();
    if (response?.success && response.data) {
      groups.value = (response.data.groups || []).map((g) =>
        mapEntity(g, "groupe"),
      );
      services.value = (response.data.services || []).map((s) =>
        mapEntity(s, "service"),
      );
    }
  } catch (e) {
    console.error("Error loading stats by entity:", e);
    error.value = "Erreur lors du chargement des statistiques.";
  } finally {
    loading.value = false;
    await nextTick();
    checkScrollable();
  }
};

onMounted(() => {
  loadData();
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
